/**
 * OTP verification component
 */

import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * OTP verification component
 * 
 * @param {Object} props - Component props
 * @param {string} props.email - User email
 * @param {Function} props.onVerified - Function to call when OTP is verified
 * @param {Function} props.resendOTP - Function to resend OTP
 * @returns {JSX.Element} OTP verification component
 */
const OTPVerification = ({ email, onVerified, resendOTP }) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Start countdown for resend OTP
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle OTP change
  const handleChange = (e) => {
    // Allow only numbers
    const value = e.target.value.replace(/[^0-9]/g, '');
    setOtp(value);
    
    // Clear error when typing
    if (error) setError('');
  };

  // Handle OTP submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!otp) {
      setError('Please enter verification code');
      return;
    }
    
    setLoading(true);
    try {
      await onVerified(otp);
    } catch (error) {
      setError(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP resend
  const handleResendOTP = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      await resendOTP();
      setCountdown(60);
      setCanResend(false);
    } catch (error) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="font-medium text-gray-900">Email Verification</h3>
        <p className="text-sm text-gray-500 mt-1">
          We've sent a 6-digit code to <span className="font-medium">{email}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <input
            type="text"
            value={otp}
            onChange={handleChange}
            maxLength={6}
            placeholder="Enter 6-digit code"
            className={`py-3 px-4 w-full text-center text-lg bg-gray-50 border ${
              error ? 'border-red-500' : 'border-gray-200'
            } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        <button
          type="submit"
          disabled={loading || otp.length !== 6}
          className={`w-full font-medium py-3 rounded-xl flex items-center justify-center transition-colors ${
            loading || otp.length !== 6
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-green-500 hover:bg-green-600 text-white'
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span>Verifying...</span>
            </>
          ) : (
            <span>Verify Code</span>
          )}
        </button>

        <div className="text-center">
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={!canResend || loading}
            className={`text-sm ${
              canResend && !loading
                ? 'text-green-500 hover:text-green-700'
                : 'text-gray-400'
            }`}
          >
            {canResend
              ? 'Resend verification code'
              : `Resend code in ${countdown}s`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OTPVerification;